import * as yup from 'yup';

const initialValues = {
  travelType: '',
  travellerType: '',
  budgeted: '',
  costAssignment: '',
  sorNo: '',
  bookingAgent: '',
  travellerFirstName: '',
  travellerMiddleName: '',
  travellerLastName: '',
  purpose: '',
  comments: '',
  contactName: '',
  contactNumber: '',
  cityTourCheck: false,
  dinnerReservations: false,
  specialPresentations: false,
  requirements: {
    flight: true,
    hotel: false,
    airport: false,
    insurance: false,
    visa: false,
  },
  familyInformation: [],
  flightInformation: [],
  hotelInformation: [],
  clientInformation: [],
};

const flightInformationValidationSchema = yup.object().shape({
  fromCity: yup.object().shape({
    value: yup.string().required('From City is required'),
    label: yup.string().required('From City is required'),
  }),
  toCountry: yup.object().shape({
    value: yup.string().required('To Country is required'),
    label: yup.string().required('To Country is required'),
  }),
  toCity: yup.object().shape({
    value: yup.string().required('To City is required'),
    label: yup.string().required('To City is required'),
  }),
  date: yup
    .date()
    .transform((_value, originalValue) => {
      if (!originalValue) return null;
      const date = new Date(originalValue);
      return Number.isNaN(date) ? null : date;
    })
    .required('Date is required')
    .nullable()
    .typeError('Please enter a valid date')
    .min(
      new Date(new Date().setHours(0, 0, 0, 0)),
      'Date cannot be in the past',
    ),

  time: yup
    .string()
    .nullable()
    .matches(
      /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
      'Please enter a valid time in 24-hour format (HH:MM)',
    ),
  airline: yup.object().nullable(),
  class: yup.string().nullable(),
  traveller: yup.string().nullable(),
  isRiskCountry: yup.string().nullable(),
});

const hotelValidationSchema = yup.object().shape({
  country: yup.object().shape({
    value: yup.string().required('Country is required'),
    label: yup.string().required('Country is required'),
  }),
  place: yup.string().required('Place is required'),
  checkInDate: yup
    .string()
    .required('Check-In Date is required')
    .test('not-in-past', 'Check-In Date cannot be in the past', value => {
      if (!value) return true;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return new Date(value) >= today;
    }),
  checkOutDate: yup
    .string()
    .required('Check-Out Date is required')
    .test(
      'is-after-checkin',
      'Check-Out Date cannot be before Check-In Date',
      function (value) {
        const { checkInDate } = this.parent;
        if (!checkInDate || !value) return true;
        return new Date(value) >= new Date(checkInDate);
      },
    )
    .test('not-in-past', 'Check-In Date cannot be in the past', value => {
      if (!value) return true;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return new Date(value) >= today;
    }),
  preferredHotel: yup.string().nullable(),
  traveller: yup.string().nullable(),
});

const clientValidationSchema = yup.object().shape({
  clientName: yup
    .string()
    .required('Client Name is required')
    .matches(/^[A-Za-z\s]+$/, 'Client name must contain only letters')
    .min(2, 'Client name must be at least 2 characters')
    .max(50, 'Client name must not exceed 50 characters'),
  company: yup
    .string()
    .required('Company is required')
    .matches(/^[A-Za-z\s]+$/, 'Company must contain only letters')
    .min(2, 'Company must be at least 2 characters')
    .max(50, 'Company must not exceed 50 characters'),
  contactNumber: yup
    .string()
    .required('Contact Number is required')
    .matches(/^[0-9+\-\s()]+$/, 'Please enter a valid phone number')
    .min(8, 'Contact number must be at least 8 digits')
    .max(15, 'Contact number must not exceed 15 digits'),
  projectName: yup
    .string()
    .required('Project Name is required')
    .min(2, 'Project name must be at least 2 characters')
    .max(50, 'Project name must not exceed 50 characters'),
  publicOfficial: yup.string().nullable(),
});

const validationSchema = yup.object().shape({
  travelType: yup.string().required('Travel type is required').nullable(),
  travellerType: yup.string().required('Traveller type is required'),
  budgeted: yup.string().required('Budgeted is required').nullable(),
  costAssignment: yup
    .string()
    .required('Cost assignment is required')
    .nullable(),
  serviceOrder: yup
    .mixed()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .when('costAssignment', {
      is: 'B1C04',
      then: schema =>
        schema
          .required('Service Order is required')
          .typeError('Service Order is required'),
      otherwise: schema => schema.notRequired(),
    }),
  sorNo: yup
    .mixed()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .when('costAssignment', {
      is: 'B1C03',
      then: schema =>
        schema.required('SOR No. is required').typeError('SOR No. is required'),
      otherwise: schema => schema.notRequired(),
    }),
  department: yup.string().when('costAssignment', {
    is: val => val === 'B1C02' || val === 'T1C04',
    then: schema => schema.required('Department is required'),
    otherwise: schema => schema.notRequired(),
  }),
  bookingAgent: yup.string().required('Booking agent is required'),
  travellerFirstName: yup
    .string()
    .required('Traveller first name is required')
    .matches(/^[A-Za-z\s]+$/, 'First name must contain only letters')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must not exceed 50 characters'),
  travellerMiddleName: yup
    .string()
    .matches(/^[A-Za-z\s]*$/, 'Middle name must contain only letters')
    .max(50, 'Middle name must not exceed 50 characters')
    .nullable(),
  travellerLastName: yup
    .string()
    .required('Traveller last name is required')
    .matches(/^[A-Za-z\s]+$/, 'Last name must contain only letters')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must not exceed 50 characters'),
  purpose: yup
    .string()
    .required('Purpose is required')
    .min(10, 'Purpose must be at least 10 characters')
    .max(500, 'Purpose must not exceed 500 characters'),
  comments: yup
    .string()
    .max(1000, 'Comments must not exceed 1000 characters')
    .nullable(),
  contactName: yup
    .string()
    .required('Contact Name is required')
    .matches(/^[A-Za-z\s]+$/, 'Contact name must contain only letters')
    .min(2, 'Contact name must be at least 2 characters')
    .max(50, 'Contact name must not exceed 50 characters'),
  contactNumber: yup
    .string()
    .required('Contact Number is required')
    .matches(/^[0-9+\-\s()]+$/, 'Please enter a valid phone number')
    .min(8, 'Contact number must be at least 8 digits')
    .max(15, 'Contact number must not exceed 15 digits'),
  contactComments: yup.string().nullable(),
  cityTourCheck: yup.string().nullable(),
  dinnerReservations: yup.string().nullable(),
  specialPresentations: yup.string().nullable(),
  requirements: yup.object({
    flight: yup.boolean(),
    hotel: yup.boolean(),
    airport: yup.boolean(),
    insurance: yup.boolean(),
    visa: yup.boolean(),
  }),
  familyInformation: yup.array().nullable(),
  flightInformation: yup.array().when(['requirements', 'flight'], {
    is: requirements => requirements?.flight === true,
    then: () =>
      yup
        .array()
        .of(flightInformationValidationSchema)
        .min(1, 'At least one flight information is required')
        .required('Flight information is required'),
    otherwise: () => yup.array().nullable(),
  }),
  hotelInformation: yup.array().when(['requirements', 'hotel'], {
    is: requirements => requirements?.hotel === true,
    then: () =>
      yup
        .array()
        .of(hotelValidationSchema)
        .min(1, 'At least one hotel information is required')
        .required('Hotel information is required'),
    otherwise: () => yup.array().nullable(),
  }),
  clientInformation: yup.array().when('travellerType', {
    is: travellerType =>
      ['B102', 'B103', 'T102', 'T103'].includes(travellerType),
    then: () =>
      yup
        .array()
        .of(clientValidationSchema)
        .min(1, 'At least one client information is required')
        .required('Client information is required'),
    otherwise: () => yup.array().nullable(),
  }),
});

export { initialValues, validationSchema };

export const renderStatusColors = {
  New: {
    backgroundColor: '#2196F31A',
    fontColor: '#2196F3',
    borderColor: '#2196F3',
    dotColor: '#2196F3',
  },
  'Request for Quotation': {
    backgroundColor: '#0096881A',
    fontColor: '#009688',
    borderColor: '#009688',
    dotColor: '#009688',
  },
  'Pending for Decision': {
    backgroundColor: '#FF98001A',
    fontColor: '#B54708',
    borderColor: '#FF9800',
    dotColor: '#B54708',
  },
  'Pending for Submission': {
    backgroundColor: '#42A5F51A',
    fontColor: '#42A5F5',
    borderColor: '#42A5F5',
    dotColor: '#42A5F5',
  },
  'Pending for Approval': {
    backgroundColor: '#8882821A',
    fontColor: '#424242',
    borderColor: '#888282',
    dotColor: '#424242',
  },
  'Awaiting HR Approval': {
    backgroundColor: '#67A1241A',
    fontColor: '#67A124',
    borderColor: '#67A124',
    dotColor: '#67A124',
  },
  'Pending for Booking': {
    backgroundColor: '#3F51B51A',
    fontColor: '#3F51B5',
    borderColor: '#3F51B5',
    dotColor: '#3F51B5',
  },
  'Booking Confirmed': {
    backgroundColor: '#3EA9421A',
    fontColor: '#3EA942',
    borderColor: '#3EA942',
    dotColor: '#3EA942',
  },
  'Booking Cancelled': {
    backgroundColor: '#F443361A',
    fontColor: '#F44336',
    borderColor: '#F44336',
    dotColor: '#F44336',
  },
  'Request for Rebooking': {
    backgroundColor: '#9C27B01A',
    fontColor: '#9C27B0',
    borderColor: '#9C27B0',
    dotColor: '#9C27B0',
  },
  'Request for Cancellation': {
    backgroundColor: '#E91E631A',
    borderColor: '#E91E63',
    fontColor: '#E91E63',
    dotColor: '#E91E63',
  },
  Rejected: {
    backgroundColor: '#D32F2F1A',
    fontColor: '#D32F2F',
    borderColor: '#D32F2F',
    dotColor: '#D32F2F',
  },
  'Travel Cancelled': {
    backgroundColor: '#FF57221A',
    fontColor: '#FF5722',
    borderColor: '#FF5722',
    dotColor: '#FF5722',
  },
};

export const FIELD_ORDER = [
  'travelType',
  'travellerType',
  'budgeted',
  'costAssignment',
  'serviceOrder',
  'sorNo',
  'department',
  'bookingAgent',
  'travellerFirstName',
  'travellerMiddleName',
  'travellerLastName',
  'purpose',
  'comments',
  'contactName',
  'contactNumber',
  'contactComments',
  'cityTourCheck',
  'dinnerReservations',
  'specialPresentations',
  'requirements',
  'familyInformation',
  'flightInformation',
  'hotelInformation',
  'clientInformation',
];

export const FLIGHT_ITEM_FIELDS = [
  'fromCity',
  'toCountry',
  'toCity',
  'date',
  'time',
  'airline',
  'class',
  'traveller',
  'isRiskCountry',
];

export const HOTEL_ITEM_FIELDS = [
  'country',
  'place',
  'checkInDate',
  'checkOutDate',
  'preferredHotel',
  'traveller',
];

export const CLIENT_ITEM_FIELDS = [
  'clientName',
  'company',
  'contactNumber',
  'projectName',
  'publicOfficial',
];
